import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiResponse } from "../../../types/APIResponse";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { Place } from "../../../types";

export interface DeletePlace extends ApiResponse<any> {
    id: string;
}

// Fetch all places
export const fetchPlaces = createAsyncThunk<
    Place[],
    { establishmentLocationId?: string; status?: number },
    { rejectValue: string }
>(
    'place/fetchAll',
    async ({ establishmentLocationId, status }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (establishmentLocationId) params.append('establishmentLocationId', establishmentLocationId);
            if (status !== undefined) params.append('status', status.toString());

            const response = await api.get<ApiResponse<Place[]>>(
                `${apiEndpoints.place.getAll}?${params.toString()}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch places');
        }
    }
);

// Fetch single place
export const fetchSinglePlace = createAsyncThunk<
    Place,
    string,
    { rejectValue: string }
>(
    'place/fetchSingle',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Place>>(
                `${apiEndpoints.place.get}/${id}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch place');
        }
    }
);

// Add place
export const addPlace = createAsyncThunk<
    Place,
    { formData: FormData },
    { rejectValue: string }
>(
    'place/add',
    async ({ formData }, { rejectWithValue }) => {
        try {
            const response = await apiWithFormDataMethods.post<ApiResponse<Place>>(
                apiEndpoints.place.create,
                formData
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add place');
        }
    }
);

// Update place
export const updatePlace = createAsyncThunk<
    Place,
    { id: string; formData: FormData },
    { rejectValue: string }
>(
    'place/update',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await apiWithFormDataMethods.put<ApiResponse<Place>>(
                `${apiEndpoints.place.update}/${id}`,
                formData
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update place');
        }
    }
);

// Delete place
export const deletePlace = createAsyncThunk<
    DeletePlace,
    { id: string },
    { rejectValue: string }
>(
    'place/delete',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.delete<ApiResponse<DeletePlace>>(
                `${apiEndpoints.place.delete}/${id}`
            );
            return { ...response.data, id };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete place');
        }
    }
);

// Update order
export const updatePlaceOrder = createAsyncThunk<
    Place[],
    { orderUpdates: Array<{ id: string; index: number }> },
    { rejectValue: string }
>(
    'place/updateOrder',
    async ({ orderUpdates }, { rejectWithValue }) => {
        try {
            const response = await api.put<ApiResponse<Place[]>>(
                apiEndpoints.place.updateOrder,
                { data: { orderUpdates } }
            );
            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to update order');
            }
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update order');
        }
    }
);
